<template>
  <div>
    <AuBreadcrumb :pages="breadcrumb" />
    <Form>
      <template #title>
        {{ $t("convertToken.title") }}
      </template>

      <div class="convert-box">
        <div class="input-block">
          <span class="label-text">
            {{ $t("convertToken.from") }}
          </span>
          <div>
            <AuSelect
              :value="tokenFromId"
              :options="tokensFromOptions"
              @input="(v) => (tokenFromId = v)"
            />
            <span
              v-if="available"
              class="fs12"
            >
              {{
                $t("convertToken.available") +
                  ` ${available} ${tokenFromMnemonic}`
              }}
            </span>
          </div>
        </div>
        <div class="input-block">
          <span class="label-text">
            {{ $t("convertToken.to") }}
          </span>
          <AuSelect
            class="receive"
            :value="tokenToId"
            :options="tokensToOptions"
            @input="(v) => (tokenToId = v)"
          />
        </div>
        <div
          v-if="showAmount"
          class="convert-box"
        >
          <div class="input-block">
            <AuInput
              v-model="amount"
              :label="$t('convertToken.amount')"
              @input="checkInput"
            />
            <div
              v-if="course"
              class="description"
            >
              <div>
                {{ $t("convertToken.rate") + ": " }}
                <span class="course-text">
                  {{ `1 ${tokenFromMnemonic} ⟷ ${course} ${tokenToMnemonic}` }}
                </span>
              </div>
              <div class="exception">
                {{ $t("convertToken.commission") + ": " }}
                <span class="course-text">
                  {{ commission }}%
                  <span v-if="amount > 0">
                    ({{ remainder }} {{ tokenToMnemonic }})
                  </span>
                </span>
              </div>
              <div>
                {{ $t("convertToken.willReceive") + ": " }}
                <span class="course-text">
                  {{ receive + " " + tokenToMnemonic }}
                </span>
              </div>
            </div>
            <div
              v-else
              class="description"
            >
              <span class="error-msg"> {{ $t("convertToken.noRate") }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="error?.length > 0"
          class="error-msg exchange-place"
        >
          {{ error }}
        </div>
      </div>

      <template #footer>
        <AuButton
          v-if="showAmount"
          :disabled="!course"
          type="primary"
          center
          :is-progress="disable"
          @click="getExchange"
        >
          {{ $t("convertToken.exchange") }}
        </AuButton>
      </template>
    </Form>
  </div>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import CurrencySupport from "@/help/currencySupport";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ConvertToken",
  data() {
    return {
      breadcrumb: [
        { key: 0, name: this.$t("router.pages.dashboard"), path: "/" },
        { key: 1, name: this.$t("router.pages.briefcase"), path: "/my-assets" }],
      tokenFromId: null,
      tokenToId: null,
      amount: "",
      rate: 0,
      commission: "0",
      currencySup: new CurrencySupport(),
      error: "",
      available: null,
      disable: false
    };
  },
  computed: {
    ...mapGetters({
      defaultTokenId: "defaultToken/defaultTokenId",
      defaultTokenMnemonic: "defaultToken/defaultTokenMnemonic",
      defaultAutMnemonic: "defaultToken/defaultAutMnemonic",
      tokensUtility: "tokensUtility",
      ethAddresses: "ethAddresses"
    }),

    tokensFromOptions() {
      return [{ display: this.defaultTokenMnemonic, value: this.defaultTokenId }];
    },

    tokensToOptions() {
      const autTokenId = _.find(this.tokensUtility, token => token.mnemonicEng === this.defaultAutMnemonic)?.id;
      return autTokenId ? [{
        display: this.defaultAutMnemonic,
        value: autTokenId
      }] : [];
    },

    tokenFromMnemonic() {
      return _.find(this.tokensFromOptions, token => token.value === this.tokenFromId)?.display;
    },

    tokenToMnemonic() {
      return _.find(this.tokensToOptions, token => token.value === this.tokenToId)?.display;
    },

    showAmount() {
      return this.tokenFromId && this.tokenToId;
    },

    course() {
      if (this.rate && this.rate !== "Infinity") {
        return BigNumber(this.rate).toFixed(4);
      }
      else {
        return null;
      }
    },

    receive() {
      const buf = BigNumber(this.amount).minus(BigNumber(this.amount).multipliedBy(this.commission).dividedBy(100));
      return buf.isNaN() ? "" : buf.multipliedBy(this.rate).toFixed(2);
    },

    remainder() {
      const total = BigNumber(this.amount).multipliedBy(this.rate);
      const commissionAmount = total.multipliedBy(BigNumber(this.commission).dividedBy(100));
      return commissionAmount.toFixed(4);
    }
  },

  watch: {
    async tokenFromId(tokenIdNew) {
      if (this.tokenToId) {
        this.getRate();
      }
      await this.getAvailableSum(tokenIdNew);
    },

    tokenToId() {
      if (this.tokenFromId) {
        this.getRate();
      }
    }
  },

  async mounted() {
    const resp = await this.getSystemSettings();
    this.commission = resp.data?.systemConversationCommission ?? "0";
  },

  methods: {
    ...mapActions({
      conversion: "conversion",
      getAuOwnerByTokenId: "getAuOwnerByTokenId",
      getSystemSettings: "getSystemSettings"
    }),

    async getRate() {
      this.rate = (await this.currencySup.convertCurrencyRate({ tokenToId: this.tokenToId, tokenFromId: this.tokenFromId })).rate;
    },

    checkInput() {
      this.amount = this.amount.match(/[0-9]*/);
    },

    async getAvailableSum(tokenId) {
      try {
        const resp = await this.getAuOwnerByTokenId({
          tokenId: tokenId,
          ownerAddress: this.ethAddresses[0].value
        });
        const available = resp[0].amount;
        const decimals = resp[0].auToken.decimals;
        let buf = BigNumber(available).dividedBy(BigNumber(10).pow(decimals));
        this.available = buf.isNaN() ? 0 : buf.toString(10);
      }
      catch (e) {
        this.available = 0;
      }
    },

    async getExchange() {
      this.disable = true;
      this.error = "";
      const params = {
        amount: this.amount.toString(),
        sendToken: `/api/au_tokens/${this.tokenFromId}`,
        recvToken: `/api/au_tokens/${this.tokenToId}`
      };
      try {
        const resp = await this.conversion(params);
        if (resp.status !== 200) {
          this.error = resp?.data?.message;
        }
        else {
          this.$toast.success(this.$t("convertToken.success"));
          this.disable = false;
          this.$router.push({ name: "briefcase" });
        }
      }
      catch (e) {
        this.error = e?.response?.data?.message;
        console.error(e);
      }
      this.disable = false;
    }
  }
};
</script>

<style scoped lang="scss">
.label-text {
  display: block;
  margin-bottom: 8px;
  color: var(--color-50);
}

.course-text {
  color: #279063;
}

.convert-box {
  display: flex;
  flex-direction: column;
}
.input-block {
  padding-bottom: 25px;
}
.fs12 {
  display: block;
  margin-top: 8px;
}
.description {
  margin-top: 8px;
}

.error-msg {
  color: red;
  font-size: 14px;
}

.exchange-place {
  display: flex;
  justify-content: center;
}
</style>